import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  LocalDrink,
  Assignment,
  Timer,
  CheckCircle,
  Warning,
  TrendingUp,
  Assessment,
  Refresh,
  Visibility,
  Analytics,
  Speed,
  Opacity,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import samplesService, { Sample, SampleStatus } from '../../services/samplesService';

interface VolumetrieValidationData {
  sample_id: string;
  validation_notes: string;
  validation_status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'REQUIRES_REANALYSIS';
  validator_id: string;
  validator_name: string;
  validation_date: string;
  quality_issues?: string[];
  precision_check?: boolean;
}

const VolumétrieSupervisorDashboard: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const [samples, setSamples] = useState<Sample[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSample, setSelectedSample] = useState<Sample | null>(null);
  const [validationDialog, setValidationDialog] = useState(false);
  const [validationNotes, setValidationNotes] = useState('');

  const [stats, setStats] = useState({
    pending_validation: 0,
    validated_today: 0,
    rejected_samples: 0,
    average_precision: 0,
    total_titrations: 0,
    reanalysis_required: 0,
  });

  useEffect(() => {
    loadVolumétrieSupervisorData();
    const interval = setInterval(loadVolumétrieSupervisorData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadVolumétrieSupervisorData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      
      // First get the total count, then fetch all if reasonable
      const countResponse = await samplesService.getSamples(1, 1);
      const total = countResponse.total || 0;
      
      // Fetch all samples (up to 10000 for performance)
      const limit = Math.min(total, 10000);
      const response = await samplesService.getSamples(1, limit || 1000);
      const allSamples = response.samples || response;
      const volumetrieSamples = allSamples.filter((sample: any) => 
        sample.statut === 'VALIDATION_SERVICE' || 
        sample.current_laboratory === 'LABORATOIRE_VOLUMETRIE' ||
        sample.selected_laboratory_methods?.some((method: string) => method.includes('VOLUMETRIE'))
      );
      
      setSamples(volumetrieSamples);
      
      const pendingValidation = volumetrieSamples.filter((s: any) => s.statut === 'VALIDATION_SERVICE').length;
      const validatedToday = volumetrieSamples.filter((s: any) => {
        if (!s.updated_at) return false;
        const today = new Date().toISOString().split('T')[0];
        return s.updated_at.startsWith(today) && s.statut === 'PRET';
      }).length;
      
      const rejectedSamples = volumetrieSamples.filter((s: any) => 
        s.metadonnees?.volumetrie_validation?.validation_status === 'REJECTED'
      ).length;

      setStats({
        pending_validation: pendingValidation,
        validated_today: validatedToday,
        rejected_samples: rejectedSamples,
        average_precision: 98.2,
        total_titrations: validatedToday * 4,
        reanalysis_required: 0,
      });

    } catch (error) {
      console.error('❌ Error loading Volumétrie supervisor data:', error);
      setError('Erreur lors du chargement des données de supervision Volumétrie');
    } finally {
      setLoading(false);
    }
  };

  const handleViewSample = (sample: Sample) => {
    setSelectedSample(sample);
    setValidationDialog(true);
  };

  const handleValidateSample = async (status: 'APPROVED' | 'REJECTED' | 'REQUIRES_REANALYSIS') => {
    if (!selectedSample || !validationNotes.trim()) {
      setError('Les notes de validation sont requises');
      return;
    }

    try {
      const validationData: VolumetrieValidationData = {
        sample_id: selectedSample.sample_id,
        validation_notes: validationNotes,
        validation_status: status,
        validator_id: user?.id || '',
        validator_name: `${user?.firstName} ${user?.lastName}`,
        validation_date: new Date().toISOString(),
      };

      const updatedSample = {
        ...selectedSample,
        statut: status === 'APPROVED' ? 'PRET' as SampleStatus : 
                status === 'REQUIRES_REANALYSIS' ? 'LABORATOIRE_VOLUMETRIE_ANALYSE' as SampleStatus :
                'REJECTED' as SampleStatus,
        metadonnees: {
          ...selectedSample.metadonnees,
          volumetrie_validation: validationData,
        }
      };

      await samplesService.updateSample(selectedSample.id, updatedSample);
      
      loadVolumétrieSupervisorData();
      setValidationDialog(false);
      setSelectedSample(null);
      setValidationNotes('');
      
    } catch (error) {
      console.error('Error validating Volumétrie sample:', error);
      setError('Erreur lors de la validation de l\'échantillon Volumétrie');
    }
  };

  const extractVolumetrieResults = (sample: Sample) => {
    const analysisForm = sample.metadonnees?.volumetrie_analysis_form;
    if (!analysisForm || !analysisForm.analysis_results) return null;
    
    return analysisForm.analysis_results.map((result: any) => ({
      analyte: result.analyte,
      method: result.method,
      volume_consumed: result.volume_consumed,
      concentration_mol_l: result.concentration_mol_l,
      concentration_percent: result.concentration_percent,
      rsd_percent: result.rsd_percent,
      quality_flag: result.quality_flag,
      endpoint_detection: result.endpoint_detection,
    }));
  };

  const getPrecisionColor = (rsd: number) => {
    if (rsd <= 2) return 'success';
    if (rsd <= 5) return 'warning';
    return 'error';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Chargement des données de supervision Volumétrie...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LocalDrink sx={{ color: '#059669' }} />
          Laboratoire Volumétrie - Supervision
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Validation des analyses volumétriques et contrôle qualité
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ 
            background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.main, 0.2)} 100%)`,
            borderLeft: `6px solid ${theme.palette.warning.main}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 30px ${alpha(theme.palette.warning.main, 0.3)}`,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: `linear-gradient(45deg, ${alpha(theme.palette.warning.main, 0.1)}, transparent)`,
              borderRadius: '50%',
              transform: 'translate(50%, -50%)',
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="h6" sx={{ fontWeight: 500 }}>
                    En Attente
                  </Typography>
                  <Typography variant="h4" component="div" sx={{
                    background: `linear-gradient(45deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                  }}>
                    {stats.pending_validation}
                  </Typography>
                </Box>
                <Box sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.8rem',
                  boxShadow: `0 8px 16px ${alpha(theme.palette.warning.main, 0.3)}`,
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'scale(1.1)' }
                }}>
                  ⏳
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ 
            background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.2)} 100%)`,
            borderLeft: `6px solid ${theme.palette.success.main}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 30px ${alpha(theme.palette.success.main, 0.3)}`,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: `linear-gradient(45deg, ${alpha(theme.palette.success.main, 0.1)}, transparent)`,
              borderRadius: '50%',
              transform: 'translate(50%, -50%)',
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="h6" sx={{ fontWeight: 500 }}>
                    Validé Aujourd'hui
                  </Typography>
                  <Typography variant="h4" component="div" sx={{
                    background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                  }}>
                    {stats.validated_today}
                  </Typography>
                </Box>
                <Box sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.8rem',
                  boxShadow: `0 8px 16px ${alpha(theme.palette.success.main, 0.3)}`,
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'scale(1.1)' }
                }}>
                  ✅
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ 
            background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.main, 0.2)} 100%)`,
            borderLeft: `6px solid ${theme.palette.error.main}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 30px ${alpha(theme.palette.error.main, 0.3)}`,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: `linear-gradient(45deg, ${alpha(theme.palette.error.main, 0.1)}, transparent)`,
              borderRadius: '50%',
              transform: 'translate(50%, -50%)',
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="h6" sx={{ fontWeight: 500 }}>
                    Rejetés
                  </Typography>
                  <Typography variant="h4" component="div" sx={{
                    background: `linear-gradient(45deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                  }}>
                    {stats.rejected_samples}
                  </Typography>
                </Box>
                <Box sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.8rem',
                  boxShadow: `0 8px 16px ${alpha(theme.palette.error.main, 0.3)}`,
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'scale(1.1)' }
                }}>
                  ❌
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ 
            background: `linear-gradient(135deg, ${alpha('#059669', 0.1)} 0%, ${alpha('#059669', 0.2)} 100%)`,
            borderLeft: `6px solid #059669`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 30px ${alpha('#059669', 0.3)}`,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: `linear-gradient(45deg, ${alpha('#059669', 0.1)}, transparent)`,
              borderRadius: '50%',
              transform: 'translate(50%, -50%)',
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="h6" sx={{ fontWeight: 500 }}>
                    Titrations
                  </Typography>
                  <Typography variant="h4" component="div" sx={{
                    background: `linear-gradient(45deg, #059669, #047857)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                  }}>
                    {stats.total_titrations}
                  </Typography>
                </Box>
                <Box sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, #059669, #047857)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.8rem',
                  boxShadow: `0 8px 16px ${alpha('#059669', 0.3)}`,
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'scale(1.1)' }
                }}>
                  🧪
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ 
            background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.main, 0.2)} 100%)`,
            borderLeft: `6px solid ${theme.palette.info.main}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 30px ${alpha(theme.palette.info.main, 0.3)}`,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: `linear-gradient(45deg, ${alpha(theme.palette.info.main, 0.1)}, transparent)`,
              borderRadius: '50%',
              transform: 'translate(50%, -50%)',
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="h6" sx={{ fontWeight: 500 }}>
                    Précision Moy.
                  </Typography>
                  <Typography variant="h4" component="div" sx={{
                    background: `linear-gradient(45deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                  }}>
                    {stats.average_precision}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    %
                  </Typography>
                </Box>
                <Box sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.8rem',
                  boxShadow: `0 8px 16px ${alpha(theme.palette.info.main, 0.3)}`,
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'scale(1.1)' }
                }}>
                  🎯
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ 
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.2)} 100%)`,
            borderLeft: `6px solid ${theme.palette.primary.main}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 30px ${alpha(theme.palette.primary.main, 0.3)}`,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.1)}, transparent)`,
              borderRadius: '50%',
              transform: 'translate(50%, -50%)',
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="h6" sx={{ fontWeight: 500 }}>
                    Efficacité
                  </Typography>
                  <Typography variant="h4" component="div" sx={{
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                  }}>
                    97%
                  </Typography>
                </Box>
                <Box sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.8rem',
                  boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'scale(1.1)' }
                }}>
                  🚀
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Échantillons Volumétrie en Validation ({samples.length})
            </Typography>
            <Button
              startIcon={<Refresh />}
              onClick={loadVolumétrieSupervisorData}
              variant="outlined"
            >
              Actualiser
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>ID Échantillon</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Analytes</TableCell>
                  <TableCell>Méthode</TableCell>
                  <TableCell>Précision (%RSD)</TableCell>
                  <TableCell>Analyste</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {samples.map((sample) => {
                  const volumetrieResults = extractVolumetrieResults(sample);
                  const avgRSD = volumetrieResults?.reduce((sum: number, r: any) => sum + (r.rsd_percent || 0), 0) / (volumetrieResults?.length || 1) || 0;
                  
                  return (
                    <TableRow key={sample.id} hover>
                      <TableCell>{sample.sample_id}</TableCell>
                      <TableCell>{sample.client?.company_name || sample.client_name || 'N/A'}</TableCell>
                      <TableCell>{sample.type_echantillon}</TableCell>
                      <TableCell>
                        <Chip
                          label={sample.statut}
                          size="small"
                          color={sample.statut === 'VALIDATION_SERVICE' ? 'warning' : 'success'}
                        />
                      </TableCell>
                      <TableCell>
                        {volumetrieResults?.map((result: any, index: number) => (
                          <Chip key={index} label={result.analyte} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                        ))}
                      </TableCell>
                      <TableCell>
                        {volumetrieResults?.map((result: any, index: number) => (
                          <Chip key={index} label={result.method} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                        ))}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${avgRSD.toFixed(1)}%`}
                          size="small"
                          color={getPrecisionColor(avgRSD)}
                        />
                      </TableCell>
                      <TableCell>
                        {sample.metadonnees?.volumetrie_analysis_form?.analyst_name || 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Valider l'échantillon">
                          <IconButton
                            size="small"
                            onClick={() => handleViewSample(sample)}
                            disabled={sample.statut !== 'VALIDATION_SERVICE'}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={validationDialog} onClose={() => setValidationDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LocalDrink sx={{ color: '#059669' }} />
            Validation Volumétrie - {selectedSample?.sample_id}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedSample && (
            <Box>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Informations Échantillon</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Client:</Typography>
                      <Typography variant="body1">{selectedSample.client?.company_name || selectedSample.client_name}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Type d'échantillon:</Typography>
                      <Typography variant="body1">{selectedSample.type_echantillon}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {extractVolumetrieResults(selectedSample) && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Résultats de Titration</Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Analyte</TableCell>
                          <TableCell>Méthode</TableCell>
                          <TableCell>Vol. Cons. (mL)</TableCell>
                          <TableCell>Conc. (mol/L)</TableCell>
                          <TableCell>Conc. (%)</TableCell>
                          <TableCell>RSD (%)</TableCell>
                          <TableCell>Qualité</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {extractVolumetrieResults(selectedSample)?.map((result: any, index: number) => (
                          <TableRow key={index}>
                            <TableCell>{result.analyte}</TableCell>
                            <TableCell>{result.method}</TableCell>
                            <TableCell>{result.volume_consumed?.toFixed(2) || 'N/A'}</TableCell>
                            <TableCell>{result.concentration_mol_l?.toFixed(4) || 'N/A'}</TableCell>
                            <TableCell>{result.concentration_percent?.toFixed(2) || 'N/A'}</TableCell>
                            <TableCell>
                              <Chip
                                label={`${result.rsd_percent?.toFixed(1)}%` || 'N/A'}
                                size="small"
                                color={getPrecisionColor(result.rsd_percent || 0)}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={result.quality_flag || 'N/A'}
                                size="small"
                                color={result.quality_flag === 'PASS' ? 'success' : result.quality_flag === 'WARNING' ? 'warning' : 'error'}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              <TextField
                fullWidth
                label="Notes de Validation *"
                multiline
                rows={4}
                value={validationNotes}
                onChange={(e) => setValidationNotes(e.target.value)}
                placeholder="Saisir les notes de validation (obligatoire)..."
                required
                error={!validationNotes.trim()}
                helperText={!validationNotes.trim() ? "Les notes de validation sont requises" : ""}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValidationDialog(false)}>Annuler</Button>
          <Button
            onClick={() => handleValidateSample('REQUIRES_REANALYSIS')}
            color="warning"
            disabled={!validationNotes.trim()}
          >
            Demander Réanalyse
          </Button>
          <Button
            onClick={() => handleValidateSample('REJECTED')}
            color="error"
            disabled={!validationNotes.trim()}
          >
            Rejeter
          </Button>
          <Button
            onClick={() => handleValidateSample('APPROVED')}
            variant="contained"
            color="success"
            disabled={!validationNotes.trim()}
          >
            Approuver
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VolumétrieSupervisorDashboard; 